import React, {useEffect, useState} from "react";
import LoadingIndicator from "../pages/LoadingIndicator";
import FamilyTree from "../ClientManagement/FamilyTree/FamilyTree";
import {useParams} from "react-router-dom";
import {RouteWithId} from "../routes/types";
import useProfileAndProposals from "../hooks/useProfileAndProposals";
import {reportingApiClient} from "./ReportingApiClient";
import {
    ClientProfilePageConfigForEstateFlowChart,
    ClientProfilePageConfigForFamilyTree,
    ClientProfilePageConfigForGoalFunding,
    FamilyTreeScreenShotDetails,
    ReportConfig
} from "./models/Reporting";
import SetPortfolioReserve from "../Goals/PortfolioReserve/SetPortfolioReserve";
import PortfolioReserve from "../Goals/PortfolioReserve/PortfolioReserve";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {
    setIsExtendedFamilySelected,
    setIsOtherMembersSelected
} from "../ClientManagement/FamilyTree/FamilyTreeSlice";
import PrintViewWrapper from "./DetailedEmailReports/PrintViewWrapper";
import AgendaContainer from "./DetailedEmailReports/AgendaDetailReport/AgendaContainer";
import CoverPage from "./CoverPage";
import DisclaimerPages from "./DisclaimerPages";
import AssetsReports from "./DetailedEmailReports/Assets";
import WealthTransferReport from "./DetailedEmailReports/WealthTransferReport/WealthTransferReport";
import {EstateType} from "../WealthTransfer/models/api";
import GoalsSummaryReport from "./GoalsSummaryReport";
import {ProfileResponse} from "../ClientManagement/models/ProfileResponse";
import ReserveAndInvestmentPlan from "./ReserveAndInvestmentPlan";
import AssetTitlingReport from "./DetailedEmailReports/AssetTitlingReport";
import {selectReleaseToggles, setReleaseToggles} from "../ReleaseToggles/releaseTogglesSlice";
import PlanSummaryWrapper from "./PlanSummaryWrapper";
import NetWorthOverTimeWrapper from "./NetWorthOverTimeReport/NetWorthOverTimeWrapper";
import AssetRelianceReports from "./DetailedEmailReports/AssetRelianceReport";
import GoalFundingWrapper from "./GoalFundingReport/GoalFundingWrapper";
import {FundingDisplayOptions} from "../Goals/Funding/models/Funding";
import WealthPlanOptimizerWrapper from "./DetailedEmailReports/WealthPlanOptimizerReport/WealthPlanOptimizerWrapper";
import ComparePlansWrapper from "./ComparePlansWrapper";
import {fetchAndSetReleaseToggles} from "../utils/LaunchDarklyReleaseTogglesUtils";
import EstateFlowchartReportWrapper from "./DetailedEmailReports/EstateFlowChartsReport/EstateFlowchartReportWrapper";
import ReviewFiduciaries from "./DetailedEmailReports/EstateFlowChartsReport/ReviewFiduciaries";
import {FiduciarySummary} from "../EstateFlowchart/model/EstateFlowchartTypes";
import {estateFlowchartApiClient} from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import {ReleaseTogglesType} from "../ReleaseToggles/ReleaseToggles";
import {splitFiduciarySummary} from "./DetailedEmailReports/EstateFlowChartsReport/reportUtil";
import {memberHasAnyFiduciaries} from "../EstateFlowchart/utils/DefUtils";
import {setIsReportingCall} from "./CreateReport/ReportingSlice";

type PageComponentProps = {
    displayName: string,
    portfolioReserveTarget: number | undefined,
    profileData: ProfileResponse,
    approvedProfileData: ProfileResponse,
    releaseToggles?: ReleaseTogglesType
}
type PageType = {
    PageComponent: React.FC<PageComponentProps>,
    isEnabled: boolean,
    familyTreeScreenShotDetails?: FamilyTreeScreenShotDetails | null
}

const FamilyTreeReport = ({familyTreeConfig}: { familyTreeConfig: ClientProfilePageConfigForFamilyTree }) => {
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (familyTreeConfig.familyTreeScreenShotDetails) {
            const {
                isOtherMembersSelected,
                isExtendedFamilySelected
            } = familyTreeConfig.familyTreeScreenShotDetails;
            dispatch(setIsOtherMembersSelected(isOtherMembersSelected));
            dispatch(setIsExtendedFamilySelected(isExtendedFamilySelected));
        }
        setIsLoading(false);
    }, [familyTreeConfig.familyTreeScreenShotDetails, dispatch]);

    if (isLoading) {
        return <LoadingIndicator/>
    }

    return <FamilyTree/>
}

export const EstateFlowChartReport = ({estateFlowChart, displayName, profile}: {
    estateFlowChart: ClientProfilePageConfigForEstateFlowChart, displayName: string,
    profile: ProfileResponse
}) => {
    const [fiduciarySummaryState, setFiduciarySummaryState] = useState<{
        fiduciarySummary?: FiduciarySummary;
        fiduciarySummaryPages: FiduciarySummary[];
    }>({fiduciarySummary: undefined, fiduciarySummaryPages: []});
    const [loading, setLoading] = useState(true);

    const enableEstateFlowchartMVPRelease = useAppSelector(selectReleaseToggles)?.enableEstateFlowchartMVPRelease;

    useEffect(() => {
        estateFlowchartApiClient.getFiduciaries(profile.id)
            .then((res) => {
            const fiduciarySummary: FiduciarySummary = res.data;
            if (fiduciarySummary?.primaryMember || fiduciarySummary?.partnerMember) {
                if (enableEstateFlowchartMVPRelease) {
                    const {pages} = splitFiduciarySummary(fiduciarySummary);
                    setFiduciarySummaryState({
                        fiduciarySummary: fiduciarySummary,
                        fiduciarySummaryPages: pages
                    })
                } else {
                    setFiduciarySummaryState({
                        fiduciarySummary: fiduciarySummary,
                        fiduciarySummaryPages: []
                    })
                }
            }
        }).catch((error) => {
            console.error("Failed to fetch fiduciary summary data", error);
            setFiduciarySummaryState({
                fiduciarySummary: undefined,
                fiduciarySummaryPages: []
            })
        }).finally(() => {
            setLoading(false);
        });
    }, [enableEstateFlowchartMVPRelease]);

    if (loading) {
        return <LoadingIndicator/>;
    }

    return (
        <>
            {
                estateFlowChart.estateFlowChartDetails!.filter(estateFlowChartDetails => estateFlowChartDetails.isEnabled)
                    .map((estateFlowChartDetails) =>
                        <EstateFlowchartReportWrapper
                            selectedFlowchartId={estateFlowChartDetails.flowchartId}
                            key={estateFlowChartDetails.flowchartId}
                            profile={profile}
                            displayName={displayName}
                        />
                    )
            }
            {enableEstateFlowchartMVPRelease
                ? (fiduciarySummaryState.fiduciarySummaryPages.length > 0 && fiduciarySummaryState.fiduciarySummaryPages.map((page, index) => {

                const isPrimaryContinued = index > 0 &&
                    memberHasAnyFiduciaries(fiduciarySummaryState.fiduciarySummary?.primaryMember);
                const isPartnerContinued = index > 0 &&
                    memberHasAnyFiduciaries(fiduciarySummaryState.fiduciarySummary?.partnerMember);

                return (
                    <PrintViewWrapper
                        key={index}
                        displayName={displayName}
                        isCallFromDEFReport={true}
                        enableEstateFlowchartMVPRelease={enableEstateFlowchartMVPRelease}
                    >
                        <ReviewFiduciaries fiduciarySummary={page}
                                           isPrimaryContinued={isPrimaryContinued}
                                           isPartnerContinued={isPartnerContinued}/>
                    </PrintViewWrapper>
                )
                })) : (fiduciarySummaryState.fiduciarySummary !== undefined &&
                <PrintViewWrapper displayName={displayName}
                                  isCallFromDEFReport={true}
                                  enableEstateFlowchartMVPRelease={enableEstateFlowchartMVPRelease}>
                    <ReviewFiduciaries fiduciarySummary={fiduciarySummaryState.fiduciarySummary}/>
                </PrintViewWrapper>)
            }
        </>
    );
}

const GoalFundingSummaryReport = ({goalFundingConfig}: {
    goalFundingConfig: ClientProfilePageConfigForGoalFunding
}) => {

    let displayOptionsForReportGeneration = FundingDisplayOptions.ONLY_PORTFOLIO;
    if (goalFundingConfig.goalFundingOptions!.displayOptions === FundingDisplayOptions.PORTFOLIO_AND_INFLOWS) {
        displayOptionsForReportGeneration = FundingDisplayOptions.PORTFOLIO_AND_INFLOWS
    } else if (goalFundingConfig.goalFundingOptions!.displayOptions === FundingDisplayOptions.PORTFOLIO_AND_NON_INVESTABLE) {
        displayOptionsForReportGeneration = FundingDisplayOptions.PORTFOLIO_AND_NON_INVESTABLE
    } else if (goalFundingConfig.goalFundingOptions!.displayOptions === FundingDisplayOptions.PORTFOLIO_AND_INFLOWS_AND_NON_INVESTABLE) {
        displayOptionsForReportGeneration = FundingDisplayOptions.PORTFOLIO_AND_INFLOWS_AND_NON_INVESTABLE
    }

    return <GoalFundingWrapper
        fundingAgeToShowReport={goalFundingConfig.goalFundingOptions?.fundingAge!}
        displayView={goalFundingConfig.goalFundingOptions?.displayView!}
        displayOptions={displayOptionsForReportGeneration} isEditPage={false}/>
}

const ReviewPortfolioReserve = ({portfolioReserveTargetLength}: {
    portfolioReserveTargetLength: number | undefined
}) => {
    const isPortfolioReserveSet = portfolioReserveTargetLength !== undefined && portfolioReserveTargetLength !== null;
    return (isPortfolioReserveSet ? <PortfolioReserve showTargetLabel={false} isReportingCall={true}/> :
        <PrintViewWrapper displayName={""}> <SetPortfolioReserve/> </PrintViewWrapper>);
}

const ReportingPrintView = () => {
    const {id} = useParams<RouteWithId>();
    const {profile, approvedProfile} = useProfileAndProposals(id);
    const displayName = approvedProfile.displayName;
    const {portfolioReserveTargetLength} = profile;
    const [pages, setPages] = useState<PageType[]>();
    const dispatch = useAppDispatch();
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        reportingApiClient.getReportConfig().then((reportConfig) => {
            dispatch(setIsReportingCall(true));
            setPages(getPages(reportConfig));
        });

        fetchAndSetReleaseToggles().then((releaseToggles) => {
            if (releaseToggles) {
                dispatch(setReleaseToggles(releaseToggles));
            }
        });
    }, []);


    if (!pages || !displayName) {
        return <LoadingIndicator/>
    }

    function getPages(reportConfig: ReportConfig): PageType[] {
        return [
            {
                PageComponent: ({displayName: displayNameForScreen}) => <PrintViewWrapper
                    displayName={displayNameForScreen}
                    showHeader={false}
                    showFooter={false}
                >
                    <CoverPage
                        displayName={displayNameForScreen}
                    />
                </PrintViewWrapper>,
                isEnabled: reportConfig.coverPage.isEnabled
            },
            {
                PageComponent: () => {
                    return <AgendaContainer/>
                },
                isEnabled: reportConfig.agenda.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <PrintViewWrapper
                    displayName={displayNameForScreen}><FamilyTreeReport
                    familyTreeConfig={reportConfig.familyTree}/></PrintViewWrapper>,
                isEnabled: reportConfig.familyTree.isEnabled,
                familyTreeScreenShotDetails: reportConfig.familyTree.familyTreeScreenShotDetails
            },
            {
                PageComponent: ({profileData, approvedProfileData}) => {
                    return <AssetsReports reportConfig={reportConfig}
                                          profile={profileData} approvedProfile={approvedProfileData}/>;
                },
                isEnabled: isAnyAssetPageEnabled(reportConfig)
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <GoalsSummaryReport reportConfig={reportConfig}
                                                                                            displayName={displayNameForScreen}/>,
                isEnabled: isAnyGoalsPageEnabled(reportConfig)
            },
            {
                PageComponent: () => <GoalFundingSummaryReport
                    goalFundingConfig={reportConfig.goalFunding!}/>
                ,
                isEnabled: reportConfig.goalFunding.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <PrintViewWrapper
                    displayName={displayNameForScreen}>
                    <SetPortfolioReserve/></PrintViewWrapper>,
                isEnabled: reportConfig.reviewPortfolioReserveDefinition.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen, portfolioReserveTarget}) =>
                    <ReviewPortfolioReserve
                        portfolioReserveTargetLength={portfolioReserveTarget}/>,
                isEnabled: reportConfig.reviewPortfolioReserve.isEnabled
            },
            {
                PageComponent: ({approvedProfileData}) => {
                    return <ReserveAndInvestmentPlan reportConfig={reportConfig}
                                                     approvedProfile={approvedProfileData}/>;
                },
                isEnabled: isAnyReserveOrInvestmentPlanPageEnabled(reportConfig)
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <PrintViewWrapper
                    displayName={displayNameForScreen}> <PlanSummaryWrapper/></PrintViewWrapper>,
                isEnabled: reportConfig.planSummary.isEnabled
            },
            {
                PageComponent: () => <ComparePlansWrapper
                    showDeltaBetweenPlansForReport={reportConfig.comparePlans.comparePlanOptions?.showDifferenceBetweenPlansForReport}
                    isEditPage={false}/>,
                isEnabled: reportConfig.comparePlans.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <WealthPlanOptimizerWrapper
                    displayName={displayNameForScreen}/>,
                isEnabled: reportConfig.wealthPlanOptimizer.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen}) => <PrintViewWrapper
                    displayName={displayNameForScreen}> <NetWorthOverTimeWrapper
                    discountRate={reportConfig.netWorthOverTime.netWorthOverTimeOptions!.discountRate}
                    isInsuranceIncluded={reportConfig.netWorthOverTime.netWorthOverTimeOptions!.insuranceIncluded}/></PrintViewWrapper>,
                isEnabled: reportConfig.netWorthOverTime.isEnabled
            },
            {
                PageComponent: ({profileData, approvedProfileData}) =>
                    <AssetRelianceReports reportConfig={reportConfig}
                                          profile={profileData} approvedProfile={approvedProfileData}/>,
                isEnabled: isAnyAssetReliancePageEnabled(reportConfig)
            },
            {
                PageComponent: () => <AssetTitlingReport reportConfig={reportConfig}/>,
                isEnabled: reportConfig.assetTitlingAndDistribution.isEnabled || reportConfig.assetTitlingAndDistributionDetailed.isEnabled
            },
            {
                PageComponent: ({profileData, approvedProfileData}) => <EstateFlowChartReport
                    estateFlowChart={reportConfig.estateFlowChart} profile={profileData}
                    displayName={approvedProfileData.displayName}/>,
                isEnabled: reportConfig.estateFlowChart.isEnabled
            },
            {
                PageComponent: ({profileData, approvedProfileData}) => <WealthTransferReport
                    estateType={EstateType.CURRENT} profile={profileData} approvedProfile={approvedProfileData}/>
                ,
                isEnabled: reportConfig.wealthTransferCurrentValueDetailed.isEnabled
            },
            {
                PageComponent: ({profileData, approvedProfileData}) => <WealthTransferReport
                    profile={profileData} approvedProfile={approvedProfileData}
                    estateType={EstateType.FUTURE}/>
                ,
                isEnabled: reportConfig.wealthTransferFutureValueDetailed.isEnabled
            },
            {
                PageComponent: ({profileData, approvedProfileData}) => <WealthTransferReport
                    profile={profileData} approvedProfile={approvedProfileData}
                    estateType={EstateType.SUNSET}/>
                ,
                isEnabled: reportConfig.wealthTransferFutureValueSunsetDetailed.isEnabled
            },
            {
                PageComponent: ({displayName: displayNameForScreen, releaseToggles: releaseToggles}) =>
                    <DisclaimerPages displayName={displayNameForScreen}
                                     releaseToggles={releaseToggles!}/>,
                isEnabled: reportConfig.disclaimerPage.isEnabled
            }
        ]
    }

    return (
        <div className='preview-and-send presentation-viewport' data-theme="print">
            {pages.map(({PageComponent, isEnabled}) => {
                if (isEnabled) {
                    return <PageComponent displayName={displayName}
                                          portfolioReserveTarget={portfolioReserveTargetLength}
                                          profileData={profile}
                                          approvedProfileData={approvedProfile}
                                          releaseToggles={releaseToggles!}
                    />
                }
                return <></>;
            })}
        </div>
    );
};

export default ReportingPrintView;

function isAnyAssetPageEnabled(reportConfig: ReportConfig): boolean {
    return reportConfig.assetsSummary.isEnabled
        || reportConfig.assetsSummaryDetailed.isEnabled
        || reportConfig.currentNetWorth.isEnabled
        || reportConfig.currentNetWorthDetailed.isEnabled
        || reportConfig.currentNetWorthAtDeath.isEnabled
        || reportConfig.currentNetWorthAtDeathDetailed.isEnabled
}

function isAnyGoalsPageEnabled(reportConfig: ReportConfig): boolean {
    return reportConfig.goalsSummary.isEnabled
        || reportConfig.goalsSummaryDetailed.isEnabled
        || reportConfig.goalsPrioritization.isEnabled
}

function isAnyReserveOrInvestmentPlanPageEnabled(reportConfig: ReportConfig): boolean {
    return reportConfig.monitorPortfolioReserve.isEnabled
        || reportConfig.currentVsProposed.isEnabled
        || reportConfig.currentVsProposedDetailed.isEnabled
}

function isAnyAssetReliancePageEnabled(reportConfig: ReportConfig): boolean {
    return reportConfig.assetReliance.isEnabled
        || reportConfig.assetRelianceDetailed.isEnabled
        || reportConfig.assetRelianceWithFutureValue.isEnabled
        || reportConfig.assetRelianceDetailedWithFutureValue.isEnabled
}